/**
 * EditTrainingArtifactDialog
 * 
 * Shared dialog for editing training artifacts.
 * Used in both TrainingCatalogAdmin and Creator Portal.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { ChevronsUpDown } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, 
  Loader2,
  Pencil,
  CheckCircle2,
  XCircle,
  Filter,
  Bot,
  Trash2,
  Wrench,
  X,
  Search,
  Upload,
  FileText,
  Wand2
} from 'lucide-react';
import { useArtifactJobMappings, useJobsCatalog, useArtifactFitment } from '@/hooks/useLearningCenter';
import { useQueryClient } from '@tanstack/react-query';
import { 
  ARTIFACT_TYPE_LABELS, 
  ACCESS_TYPE_LABELS,
  SKILL_DEPTH_LABELS,
  CONFIDENCE_LEVEL_LABELS,
  type TrainingArtifact,
  type ArtifactType,
  type AccessType,
  type SkillDepth,
  type ConfidenceLevel,
} from '@/types/training';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Slider } from '@/components/ui/slider';
import { ArtifactCapabilityEditor } from '@/components/training/ArtifactCapabilityEditor';
import { GenerateDraftQuizPanel } from '@/components/training/GenerateDraftQuizPanel';
// DTC-L: route training-artifact lifecycle write timestamps through
// the canonical clock for deterministic audit trail.
import { clock } from '@/utils/clock';

// Session storage key for persisting edit dialog state
const EDIT_ARTIFACT_STORAGE_KEY = 'training-artifact-edit-id';

interface EditTrainingArtifactDialogProps {
  artifact: TrainingArtifact;
  onUpdated: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function EditTrainingArtifactDialog({ 
  artifact,
  onUpdated,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: EditTrainingArtifactDialogProps) {
  const queryClient = useQueryClient();
  
  // Support both controlled and uncontrolled modes
  const [internalOpen, setInternalOpen] = useState(() => {
    const storedId = sessionStorage.getItem(EDIT_ARTIFACT_STORAGE_KEY);
    return storedId === artifact.id;
  });
  
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobMappingsOpen, setJobMappingsOpen] = useState(false);
  const [jobSearchOpen, setJobSearchOpen] = useState(false);
  
  // Transcript state
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptStatus, setTranscriptStatus] = useState<{ hasTranscript: boolean; source?: string; charCount?: number } | null>(null);
  const [isUploadingTranscript, setIsUploadingTranscript] = useState(false);
  const [isAnalyzingTranscript, setIsAnalyzingTranscript] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<{
    summary?: string;
    fuel_types?: string[];
    dtc_codes?: string[];
    keywords?: string[];
    job_mappings?: Array<{ job_id: string; confidence: number; mapping_kind?: string; evidence_quote?: string }>;
    skill_depth?: string;
    vehicle_fitment?: Array<{ year?: string; make?: string; model?: string; engine?: string; confidence?: string }>;
    analyzed_at?: string;
  } | null>(() => {
    const metadata = (artifact as any).analysis_metadata;
    if (metadata) {
      return {
        skill_depth: metadata.skill_depth,
        summary: metadata.has_summary ? '(saved)' : undefined,
        job_mappings: metadata.jobs_count ? Array(metadata.jobs_count).fill({}) : undefined,
        vehicle_fitment: metadata.vehicles_count ? Array(metadata.vehicles_count).fill({}) : undefined,
        dtc_codes: metadata.dtcs_count ? Array(metadata.dtcs_count).fill('') : undefined,
        keywords: metadata.keywords_count ? Array(metadata.keywords_count).fill('') : undefined,
        analyzed_at: metadata.analyzed_at,
      };
    }
    return null;
  });
  
  const [analysisJobSuggestions, setAnalysisJobSuggestions] = useState<{ 
    job_id: string; 
    confidence: number;
    relevance_weight: number;
    mapping_kind?: string;
    evidence_quote?: string;
  }[]>([]);
  
  const [rejectedProposals, setRejectedProposals] = useState<{
    job_id: string;
    job_label?: string;
    gate_failures: Array<{ gate: string; passed: boolean; reason?: string }>;
  }[]>([]);
  const [gateStats, setGateStats] = useState<{
    total_proposed: number;
    validated: number;
    rejected: number;
    rejection_by_gate: Record<string, number>;
  } | null>(null);
  const [rejectedOpen, setRejectedOpen] = useState(false);
  
  const [transcriptTextInput, setTranscriptTextInput] = useState('');
  const [isSavingTranscript, setIsSavingTranscript] = useState(false);
  const [isSuggestingTags, setIsSuggestingTags] = useState(false);
  const [suggestConfidenceNotes, setSuggestConfidenceNotes] = useState<string | null>(null);
  
  const [jobMappings, setJobMappings] = useState<{ job_id: string; relevance_weight: number }[]>([]);
  const [jobMappingsLoaded, setJobMappingsLoaded] = useState(false);
  
  const [fitmentOpen, setFitmentOpen] = useState(false);
  const [fitmentRows, setFitmentRows] = useState<{ 
    year: string; 
    make: string; 
    model: string; 
    engine: string; 
    confidence: 'explicit' | 'inferred';
    boost: number;
  }[]>([]);
  const [fitmentLoaded, setFitmentLoaded] = useState(false);
  const [newFitment, setNewFitment] = useState({ year: '', make: '', model: '', engine: '' });
  
  const [fitmentYears, setFitmentYears] = useState<number[]>([]);
  const [fitmentMakes, setFitmentMakes] = useState<string[]>([]);
  const [fitmentModels, setFitmentModels] = useState<string[]>([]);
  const [fitmentEngines, setFitmentEngines] = useState<string[]>([]);
  const [isLoadingFitmentMakes, setIsLoadingFitmentMakes] = useState(false);
  const [isLoadingFitmentModels, setIsLoadingFitmentModels] = useState(false);
  const [isLoadingFitmentEngines, setIsLoadingFitmentEngines] = useState(false);
  
  const ALL_PACKAGE_TYPES = ['Asian', 'Domestic', 'Euro', 'EV', 'Diesel', 'Exotic'];
  
  const { data: existingJobMappings } = useArtifactJobMappings(open ? artifact.id : null);
  const { data: existingFitment } = useArtifactFitment(open ? artifact.id : null);
  const { data: allJobs } = useJobsCatalog();

  useEffect(() => {
    if (existingJobMappings && open && !jobMappingsLoaded) {
      setJobMappings(existingJobMappings.map(m => ({
        job_id: m.job_id,
        relevance_weight: m.relevance_weight,
      })));
      setJobMappingsLoaded(true);
    }
  }, [existingJobMappings, open, jobMappingsLoaded]);

  useEffect(() => {
    if (existingFitment && open && !fitmentLoaded) {
      setFitmentRows(existingFitment.map(f => ({
        year: f.year?.toString() || '',
        make: f.make || '',
        model: f.model || '',
        engine: f.engine || '',
        confidence: (f.confidence as 'explicit' | 'inferred') || 'explicit',
        boost: f.boost || 40,
      })));
      setFitmentLoaded(true);
    }
  }, [existingFitment, open, fitmentLoaded]);
  
  useEffect(() => {
    const fetchYears = async () => {
      const { data, error } = await supabase.rpc('get_vehicle_catalog_years');
      if (!error && data) {
        setFitmentYears(data.map((d: { year: number }) => d.year));
      }
    };
    fetchYears();
  }, []);
  
  useEffect(() => {
    if (!newFitment.year) {
      setFitmentMakes([]);
      return;
    }
    const fetchMakes = async () => {
      setIsLoadingFitmentMakes(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
          body: { year: parseInt(newFitment.year), packageTypes: ALL_PACKAGE_TYPES }
        });
        if (!error && data?.makes) {
          setFitmentMakes(data.makes);
        }
      } finally {
        setIsLoadingFitmentMakes(false);
      }
    };
    fetchMakes();
  }, [newFitment.year]);
  
  useEffect(() => {
    if (!newFitment.year || !newFitment.make) {
      setFitmentModels([]);
      return;
    }
    const fetchModels = async () => {
      setIsLoadingFitmentModels(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-models-catalog', {
          body: { year: parseInt(newFitment.year), make: newFitment.make, packageTypes: ALL_PACKAGE_TYPES }
        });
        if (!error && data?.models) {
          setFitmentModels(data.models);
        }
      } finally {
        setIsLoadingFitmentModels(false);
      }
    };
    fetchModels();
  }, [newFitment.year, newFitment.make]);
  
  useEffect(() => {
    if (!newFitment.year || !newFitment.make || !newFitment.model) {
      setFitmentEngines([]);
      return;
    }
    const fetchEngines = async () => {
      setIsLoadingFitmentEngines(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-engines-catalog', {
          body: { year: parseInt(newFitment.year), make: newFitment.make, model: newFitment.model, packageTypes: ALL_PACKAGE_TYPES }
        });
        if (!error && data?.engines) {
          setFitmentEngines(data.engines);
        }
      } finally {
        setIsLoadingFitmentEngines(false);
      }
    };
    fetchEngines();
  }, [newFitment.year, newFitment.make, newFitment.model]);

  useEffect(() => {
    if (!open) return;
    const fetchTranscriptStatus = async () => {
      const { data, error } = await supabase
        .from('training_artifact_transcripts')
        .select('source, transcript_text')
        .eq('artifact_id', artifact.id)
        .maybeSingle();
      if (data && !error) {
        setTranscriptStatus({
          hasTranscript: true,
          source: data.source,
          charCount: data.transcript_text?.length || 0,
        });
        setTranscriptTextInput(data.transcript_text || '');
      } else {
        setTranscriptStatus({ hasTranscript: false });
        setTranscriptTextInput('');
      }
    };
    fetchTranscriptStatus();
  }, [open, artifact.id]);

  const parseTranscriptFile = (content: string, filename: string): string => {
    const ext = filename.toLowerCase().split('.').pop();
    if (ext === 'txt') return content;
    if (ext === 'vtt' || ext === 'srt') {
      const lines = content.split('\n');
      const textLines: string[] = [];
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        if (trimmed === 'WEBVTT') continue;
        if (/^\d+$/.test(trimmed)) continue;
        if (/-->/.test(trimmed)) continue;
        if (/^NOTE/.test(trimmed)) continue;
        if (/^STYLE/.test(trimmed)) continue;
        const cleanLine = trimmed.replace(/<[^>]+>/g, '');
        if (cleanLine) textLines.push(cleanLine);
      }
      return textLines.join(' ');
    }
    return content;
  };

  const handleTranscriptUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedExtensions = ['.txt', '.vtt', '.srt'];
    const ext = '.' + file.name.toLowerCase().split('.').pop();
    if (!allowedExtensions.includes(ext)) {
      toast.error('Please upload a .txt, .vtt, or .srt file');
      return;
    }
    setIsUploadingTranscript(true);
    try {
      const content = await file.text();
      const transcriptText = parseTranscriptFile(content, file.name);
      if (!transcriptText.trim()) {
        toast.error('Transcript file appears to be empty');
        return;
      }
      const { error } = await supabase
        .from('training_artifact_transcripts')
        .upsert({
          artifact_id: artifact.id,
          transcript_text: transcriptText,
          source: 'manual_upload',
          language: 'en',
          updated_at: clock.now().toISOString(),
        }, { onConflict: 'artifact_id' });
      if (error) throw error;
      setTranscriptStatus({ hasTranscript: true, source: 'manual_upload', charCount: transcriptText.length });
      setTranscriptTextInput(transcriptText);
      toast.success(`Transcript uploaded (${transcriptText.length.toLocaleString()} chars)`);
    } catch (error) {
      console.error('Failed to upload transcript:', error);
      toast.error('Failed to upload transcript');
    } finally {
      setIsUploadingTranscript(false);
      event.target.value = '';
    }
  };

  const MAX_TRANSCRIPT_CHARS = 500000;
  
  const handleSaveTranscript = async () => {
    const trimmedText = transcriptTextInput.trim();
    if (!trimmedText) {
      toast.error('Transcript is empty');
      return;
    }
    if (trimmedText.length > MAX_TRANSCRIPT_CHARS) {
      toast.error(`Transcript too large. Maximum ${MAX_TRANSCRIPT_CHARS.toLocaleString()} characters allowed.`);
      return;
    }
    setIsSavingTranscript(true);
    try {
      const { data: existing } = await supabase
        .from('training_artifact_transcripts')
        .select('artifact_id')
        .eq('artifact_id', artifact.id)
        .maybeSingle();
      let error;
      if (existing) {
        const result = await supabase
          .from('training_artifact_transcripts')
          .update({
            transcript_text: trimmedText,
            source: 'manual_upload',
            language: 'en',
            updated_at: clock.now().toISOString(),
          })
          .eq('artifact_id', artifact.id);
        error = result.error;
      } else {
        const result = await supabase
          .from('training_artifact_transcripts')
          .insert({
            artifact_id: artifact.id,
            transcript_text: trimmedText,
            source: 'manual_upload',
            language: 'en',
          });
        error = result.error;
      }
      if (error) throw error;
      setTranscriptStatus({ hasTranscript: true, source: 'manual_paste', charCount: trimmedText.length });
      toast.success(`Transcript saved (${trimmedText.length.toLocaleString()} characters)`);
    } catch (error: any) {
      console.error('Failed to save transcript:', error);
      toast.error(error?.message || 'Failed to save transcript');
    } finally {
      setIsSavingTranscript(false);
    }
  };

  const handleAnalyzeTranscript = async () => {
    setIsAnalyzingTranscript(true);
    setAnalysisResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-training-transcript', {
        body: { artifact_id: artifact.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const result = data.data;
      setAnalysisResult(result);
      
      if (result.summary && !formData.original_summary_short) {
        setFormData(prev => ({ ...prev, original_summary_short: result.summary, summary_source: 'ai_draft' }));
      }
      if (result.skill_depth) {
        setFormData(prev => ({ ...prev, skill_depth: result.skill_depth }));
      }
      if (result.fuel_types?.length > 0) {
        setFormData(prev => ({
          ...prev,
          vehicle_fuel_types: Array.from(new Set([...prev.vehicle_fuel_types, ...result.fuel_types])),
        }));
      }
      if (result.dtc_codes?.length > 0) {
        setFormData(prev => ({
          ...prev,
          dtc_codes: Array.from(new Set([...prev.dtc_codes, ...result.dtc_codes])),
        }));
      }
      if (result.keywords?.length > 0) {
        setFormData(prev => ({
          ...prev,
          keywords: Array.from(new Set([...prev.keywords, ...result.keywords])),
        }));
      }
      if (result.vehicle_fitment?.length > 0) {
        const newFitmentRows = result.vehicle_fitment
          .filter((v: { year?: string; make?: string; model?: string; engine?: string }) => {
            return !fitmentRows.some(existing => 
              existing.year === v.year && existing.make === v.make && existing.model === v.model
            );
          })
          .map((v: { year?: string; make?: string; model?: string; engine?: string }) => ({
            id: crypto.randomUUID(),
            year: v.year || '',
            make: v.make || '',
            model: v.model || '',
            engine: v.engine || '',
            boost: 40,
            confidence: 'inferred' as const,
          }));
        if (newFitmentRows.length > 0) {
          setFitmentRows(prev => [...prev, ...newFitmentRows]);
          toast.info(`Added ${newFitmentRows.length} vehicle fitment suggestion(s)`);
        }
      }
      if (result.job_mappings?.length > 0) {
        const existingJobIds = new Set(jobMappings.map(jm => jm.job_id));
        const suggestions = result.job_mappings
          .filter((m: { job_id: string }) => !existingJobIds.has(m.job_id))
          .map((m: { job_id: string; confidence: number; mapping_kind?: string; evidence_quote?: string }) => ({ 
            job_id: m.job_id, 
            confidence: m.confidence,
            relevance_weight: Math.max(0.6, Math.min(1.0, m.confidence)),
            mapping_kind: m.mapping_kind,
            evidence_quote: m.evidence_quote
          }));
        if (suggestions.length > 0) {
          setAnalysisJobSuggestions(suggestions);
          setJobMappingsOpen(true);
        }
      }
      if (result.rejected_proposals?.length > 0) {
        setRejectedProposals(result.rejected_proposals);
        setRejectedOpen(true);
      } else {
        setRejectedProposals([]);
      }
      if (result.gate_stats) {
        setGateStats(result.gate_stats);
      }
      if (result.job_mappings?.length === 0 && result.rejected_proposals?.length > 0) {
        setJobMappingsOpen(true);
        toast.warning(`No validated job suggestions. ${result.rejected_proposals.length} proposals rejected by gates.`, {
          description: 'See "Rejected Proposals" for details.',
        });
      }
      setFilteringOpen(true);
      
      const analysisMetadata = {
        skill_depth: result.skill_depth || null,
        has_summary: !!result.summary,
        jobs_count: result.job_mappings?.length || 0,
        vehicles_count: result.vehicle_fitment?.length || 0,
        dtcs_count: result.dtc_codes?.length || 0,
        keywords_count: result.keywords?.length || 0,
        analyzed_at: clock.now().toISOString(),
      };
      await supabase.from('training_artifacts').update({ analysis_metadata: analysisMetadata }).eq('id', artifact.id);
      
      const parts = [
        result.job_mappings?.length > 0 ? `${result.job_mappings.length} jobs` : '',
        result.keywords?.length > 0 ? `${result.keywords.length} keywords` : '',
        result.skill_depth ? `skill: ${result.skill_depth}` : '',
        result.vehicle_fitment?.length > 0 ? `${result.vehicle_fitment.length} vehicles` : '',
      ].filter(Boolean);
      toast.success(`Analysis complete: ${parts.join(', ')}`, {
        description: `Processed ${(result.metadata?.transcript_char_count / 1000).toFixed(0)}k chars in ${(result.metadata?.processing_time_ms / 1000).toFixed(1)}s`,
      });
    } catch (error) {
      console.error('Transcript analysis failed:', error);
      const err = error as { status?: number; context?: { status?: number }; message?: string };
      const httpStatus = err.status || err.context?.status;
      const errorMsg = err.message || '';
      if (httpStatus === 429 || errorMsg.includes('429') || errorMsg.includes('rate_limit')) {
        toast.error("We're temporarily rate-limited", { description: 'Please wait 30-60 seconds and try again.', duration: 8000 });
      } else if (httpStatus === 402 || errorMsg.includes('402') || errorMsg.includes('credit')) {
        toast.error('AI credits exhausted', { description: 'Please add credits to continue.', duration: 8000 });
      } else {
        let userMessage = 'Analysis failed';
        try {
          const jsonMatch = errorMsg.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            userMessage = parsed.message || parsed.error || userMessage;
          } else {
            userMessage = errorMsg || userMessage;
          }
        } catch {
          userMessage = errorMsg || userMessage;
        }
        if (userMessage.includes('non-2xx status code')) userMessage = 'Analysis failed. Please try again.';
        toast.error(userMessage);
      }
    } finally {
      setIsAnalyzingTranscript(false);
    }
  };

  const handleAutoSuggestTags = async () => {
    setIsSuggestingTags(true);
    setSuggestConfidenceNotes(null);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-training-tags', {
        body: { artifact_id: artifact.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setFormData(prev => ({
        ...prev,
        vehicle_fuel_types: data.fuel_types?.length > 0 
          ? Array.from(new Set([...prev.vehicle_fuel_types, ...data.fuel_types]))
          : prev.vehicle_fuel_types,
        dtc_codes: data.dtc_codes?.length > 0 
          ? Array.from(new Set([...prev.dtc_codes, ...data.dtc_codes]))
          : prev.dtc_codes,
        keywords: data.keywords?.length > 0 
          ? Array.from(new Set([...prev.keywords, ...data.keywords]))
          : prev.keywords,
      }));
      setFilteringOpen(true);
      if (data.meta?.confidence_notes) setSuggestConfidenceNotes(data.meta.confidence_notes);
      const counts = [
        data.fuel_types?.length > 0 ? `${data.fuel_types.length} fuel types` : '',
        data.dtc_codes?.length > 0 ? `${data.dtc_codes.length} DTCs` : '',
        data.keywords?.length > 0 ? `${data.keywords.length} keywords` : '',
      ].filter(Boolean);
      if (counts.length > 0) {
        toast.success(`Suggested: ${counts.join(', ')}`, {
          description: data.meta?.transcript_used ? 'Based on metadata + transcript' : 'Based on metadata only',
        });
      } else {
        toast.info('No new suggestions found');
      }
    } catch (error) {
      console.error('Failed to suggest tags:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to suggest tags');
    } finally {
      setIsSuggestingTags(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('training-catalog-admin', {
        body: { action: 'archive', artifactId: artifact.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Artifact removed from catalog');
      sessionStorage.removeItem(EDIT_ARTIFACT_STORAGE_KEY);
      setDeleteDialogOpen(false);
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['training-artifacts'] });
      await queryClient.invalidateQueries({ queryKey: ['creator-artifacts'] });
      onUpdated();
    } catch (error) {
      console.error('Failed to remove artifact:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to remove artifact');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const getInitialAccessUrl = () => {
    if (artifact.access_url) return artifact.access_url;
    if (!['book', 'ebook'].includes(artifact.artifact_type) && artifact.purchase_url) {
      return artifact.purchase_url;
    }
    return '';
  };
  
  const [formData, setFormData] = useState({
    title: artifact.title,
    provider_name: artifact.provider_name || '',
    instructor_name: artifact.instructor_name || '',
    artifact_type: artifact.artifact_type,
    access_url: getInitialAccessUrl(),
    access_type: artifact.access_type,
    estimated_time_minutes: artifact.estimated_time_minutes?.toString() || '',
    skill_depth: artifact.skill_depth,
    confidence_level: artifact.confidence_level,
    delivery_format: artifact.delivery_format,
    travel_required: artifact.travel_required,
    original_summary_short: artifact.original_summary_short || '',
    summary_source: (artifact as any).summary_source || null as string | null,
    is_active: artifact.is_active,
    purchase_url: artifact.purchase_url || '',
    purchase_provider_name: artifact.purchase_provider_name || '',
    is_reference_only: artifact.is_reference_only || false,
    vehicle_fuel_types: (artifact as any).vehicle_fuel_types || [] as string[],
    dtc_codes: (artifact as any).dtc_codes || [] as string[],
    keywords: (artifact as any).keywords || [] as string[],
  });
  
  const [newDtcCode, setNewDtcCode] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [filteringOpen, setFilteringOpen] = useState(false);

  useEffect(() => {
    if (open) sessionStorage.setItem(EDIT_ARTIFACT_STORAGE_KEY, artifact.id);
  }, [open, artifact.id]);

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setFormData({
        title: artifact.title,
        provider_name: artifact.provider_name || '',
        instructor_name: artifact.instructor_name || '',
        artifact_type: artifact.artifact_type,
        access_url: getInitialAccessUrl(),
        access_type: artifact.access_type,
        estimated_time_minutes: artifact.estimated_time_minutes?.toString() || '',
        skill_depth: artifact.skill_depth,
        confidence_level: artifact.confidence_level,
        delivery_format: artifact.delivery_format,
        travel_required: artifact.travel_required,
        original_summary_short: artifact.original_summary_short || '',
        summary_source: (artifact as any).summary_source || null,
        is_active: artifact.is_active,
        purchase_url: artifact.purchase_url || '',
        purchase_provider_name: artifact.purchase_provider_name || '',
        is_reference_only: artifact.is_reference_only || false,
        vehicle_fuel_types: (artifact as any).vehicle_fuel_types || [],
        dtc_codes: (artifact as any).dtc_codes || [],
        keywords: (artifact as any).keywords || [],
      });
      setJobMappingsLoaded(false);
      setJobMappings([]);
      setFitmentLoaded(false);
      setFitmentRows([]);
      setNewFitment({ year: '', make: '', model: '', engine: '' });
      setNewDtcCode('');
      setNewKeyword('');
      const metadata = (artifact as any).analysis_metadata;
      if (metadata) {
        setAnalysisResult({
          skill_depth: metadata.skill_depth,
          summary: metadata.has_summary ? '(saved)' : undefined,
          job_mappings: metadata.jobs_count ? Array(metadata.jobs_count).fill({}) : undefined,
          vehicle_fitment: metadata.vehicles_count ? Array(metadata.vehicles_count).fill({}) : undefined,
          dtc_codes: metadata.dtcs_count ? Array(metadata.dtcs_count).fill('') : undefined,
          keywords: metadata.keywords_count ? Array(metadata.keywords_count).fill('') : undefined,
          analyzed_at: metadata.analyzed_at,
        });
      } else {
        setAnalysisResult(null);
      }
      sessionStorage.setItem(EDIT_ARTIFACT_STORAGE_KEY, artifact.id);
    } else {
      sessionStorage.removeItem(EDIT_ARTIFACT_STORAGE_KEY);
    }
    setOpen(newOpen);
  };
  
  const handleAddFitment = () => {
    if (!newFitment.make && !newFitment.model && !newFitment.year) return;
    setFitmentRows(prev => [...prev, { 
      year: newFitment.year,
      make: newFitment.make,
      model: newFitment.model,
      engine: newFitment.engine,
      confidence: 'explicit',
      boost: 40,
    }]);
    setNewFitment({ year: '', make: '', model: '', engine: '' });
  };
  
  const handleRemoveFitment = (index: number) => {
    setFitmentRows(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleAddJobMapping = (jobId: string) => {
    if (jobMappings.some(m => m.job_id === jobId)) return;
    setJobMappings(prev => [...prev, { job_id: jobId, relevance_weight: 1.0 }]);
  };
  
  const handleRemoveJobMapping = (jobId: string) => {
    setJobMappings(prev => prev.filter(m => m.job_id !== jobId));
  };
  
  const handleUpdateJobWeight = (jobId: string, weight: number) => {
    setJobMappings(prev => prev.map(m => m.job_id === jobId ? { ...m, relevance_weight: weight } : m));
  };
  
  const getJobLabel = (jobId: string) => {
    const job = allJobs?.find(j => j.id === jobId);
    return job ? `${job.label} (${job.category_key})` : jobId;
  };
  
  const availableJobs = allJobs?.filter(j => !jobMappings.some(m => m.job_id === j.id)) || [];

  const handleTypeChange = (newType: ArtifactType) => {
    const oldType = formData.artifact_type;
    const isOldBook = ['book', 'ebook'].includes(oldType);
    const isNewBook = ['book', 'ebook'].includes(newType);
    if (isOldBook && !isNewBook) {
      setFormData(prev => ({ ...prev, artifact_type: newType, access_url: prev.access_url || prev.purchase_url }));
    } else if (!isOldBook && isNewBook) {
      setFormData(prev => ({ ...prev, artifact_type: newType, purchase_url: prev.purchase_url || prev.access_url }));
    } else {
      setFormData(prev => ({ ...prev, artifact_type: newType }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const updateData: Record<string, unknown> = {
        title: formData.title,
        provider_name: formData.provider_name || null,
        instructor_name: formData.instructor_name || null,
        artifact_type: formData.artifact_type,
        access_url: formData.access_url || null,
        access_type: formData.access_type,
        estimated_time_minutes: formData.estimated_time_minutes ? parseInt(formData.estimated_time_minutes) : null,
        skill_depth: formData.skill_depth,
        confidence_level: formData.confidence_level,
        delivery_format: formData.delivery_format,
        travel_required: formData.travel_required,
        original_summary_short: formData.original_summary_short || null,
        summary_source: formData.summary_source,
        is_active: formData.is_active,
        vehicle_fuel_types: formData.vehicle_fuel_types.length > 0 ? formData.vehicle_fuel_types : null,
        dtc_codes: formData.dtc_codes.length > 0 ? formData.dtc_codes : null,
        keywords: formData.keywords.length > 0 ? formData.keywords : null,
      };
      const isBook = formData.artifact_type === 'book' || formData.artifact_type === 'ebook';
      if (isBook) {
        updateData.purchase_url = formData.purchase_url || null;
        updateData.purchase_provider_name = formData.purchase_provider_name || null;
        updateData.is_reference_only = formData.is_reference_only;
        updateData.access_url = null;
      } else {
        updateData.purchase_url = null;
        updateData.purchase_provider_name = null;
        updateData.is_reference_only = false;
      }
      const hasPendingFitment = !!newFitment.year || newFitment.make.trim() !== '' || newFitment.model.trim() !== '';
      const fitmentRowsForPayload = hasPendingFitment
        ? [...fitmentRows, { year: newFitment.year, make: newFitment.make.trim(), model: newFitment.model.trim(), engine: newFitment.engine.trim(), confidence: 'explicit' as const, boost: 40 }]
        : fitmentRows;
      const fitmentPayload = fitmentRowsForPayload.map((f) => ({
        year: f.year ? parseInt(f.year) : null,
        make: f.make || null,
        model: f.model || null,
        engine: f.engine || null,
        confidence: f.confidence,
        boost: f.boost,
      }));
      const { data, error } = await supabase.functions.invoke('training-catalog-admin', {
        body: { action: 'update', artifactId: artifact.id, patch: updateData, job_mappings: jobMappings, fitment_rows: fitmentPayload },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Artifact updated');
      sessionStorage.removeItem(EDIT_ARTIFACT_STORAGE_KEY);
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['training-artifacts'] });
      await queryClient.invalidateQueries({ queryKey: ['training-artifact-job-mapping-counts'] });
      await queryClient.invalidateQueries({ queryKey: ['training-artifact-job-mappings', artifact.id] });
      await queryClient.invalidateQueries({ queryKey: ['artifact-fitment', artifact.id] });
      await queryClient.invalidateQueries({ queryKey: ['creator-artifacts'] });
      onUpdated();
    } catch (error) {
      console.error('Failed to update artifact:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update artifact');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBook = formData.artifact_type === 'book' || formData.artifact_type === 'ebook';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm">
            <Pencil className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Training Artifact</DialogTitle>
          <DialogDescription>Update artifact details and settings.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">Title *</Label>
            <Input id="edit-title" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-provider">Provider Name</Label>
              <Input id="edit-provider" value={formData.provider_name} onChange={(e) => setFormData(prev => ({ ...prev, provider_name: e.target.value }))} placeholder="e.g., brandon_steckler" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-instructor">Instructor</Label>
              <Input id="edit-instructor" value={formData.instructor_name} onChange={(e) => setFormData(prev => ({ ...prev, instructor_name: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-url">Access URL</Label>
            <Input id="edit-url" value={formData.access_url} onChange={(e) => setFormData(prev => ({ ...prev, access_url: e.target.value }))} placeholder="https://..." />
          </div>

          {isBook && (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-purchase-url">Purchase URL (AESwave, etc.)</Label>
                <Input id="edit-purchase-url" value={formData.purchase_url} onChange={(e) => setFormData(prev => ({ ...prev, purchase_url: e.target.value }))} placeholder="https://www.aeswave.com/..." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-purchase-provider">Seller Name</Label>
                <Input id="edit-purchase-provider" value={formData.purchase_provider_name} onChange={(e) => setFormData(prev => ({ ...prev, purchase_provider_name: e.target.value }))} placeholder="e.g., AESwave" />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="edit-reference-only" checked={formData.is_reference_only} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_reference_only: !!checked }))} />
                <Label htmlFor="edit-reference-only" className="text-sm">Reference only (not publicly sold)</Label>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.artifact_type} onValueChange={(v) => handleTypeChange(v as ArtifactType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ARTIFACT_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Access</Label>
              <Select value={formData.access_type} onValueChange={(v) => setFormData(prev => ({ ...prev, access_type: v as AccessType }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCESS_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Skill Depth</Label>
              <Select value={formData.skill_depth} onValueChange={(v) => setFormData(prev => ({ ...prev, skill_depth: v as SkillDepth }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SKILL_DEPTH_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Confidence</Label>
              <Select value={formData.confidence_level} onValueChange={(v) => setFormData(prev => ({ ...prev, confidence_level: v as ConfidenceLevel }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CONFIDENCE_LEVEL_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-time">Estimated Time (minutes)</Label>
            <Input id="edit-time" type="number" value={formData.estimated_time_minutes} onChange={(e) => setFormData(prev => ({ ...prev, estimated_time_minutes: e.target.value }))} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="edit-summary">OptiBay Summary <span className="text-muted-foreground ml-1">({formData.original_summary_short.length}/200)</span></Label>
              {formData.summary_source === 'ai_draft' && (
                <Badge variant="outline" className="text-xs gap-1 bg-primary/10 border-primary/30"><Bot className="h-3 w-3" />AI Draft</Badge>
              )}
            </div>
            <Textarea
              id="edit-summary"
              value={formData.original_summary_short}
              onChange={(e) => setFormData(prev => ({ ...prev, original_summary_short: e.target.value.slice(0, 200), summary_source: 'manual' }))}
              maxLength={200}
              rows={2}
              placeholder={formData.summary_source === 'ai_draft' ? 'AI-generated draft. Edit to customize.' : 'Brief summary for recommendations...'}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox id="edit-travel" checked={formData.travel_required} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, travel_required: !!checked }))} />
              <Label htmlFor="edit-travel" className="text-sm">Travel required</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="edit-active" checked={formData.is_active} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: !!checked }))} />
              <Label htmlFor="edit-active" className="text-sm">Active</Label>
            </div>
          </div>

          {/* Transcript Upload Section */}
          <Collapsible open={transcriptOpen} onOpenChange={setTranscriptOpen} className="border rounded-lg">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors text-left">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Transcript</span>
                  {transcriptStatus?.hasTranscript && (
                    <Badge variant="outline" className="text-xs gap-1 bg-green-500/10 border-green-500/30 text-green-700">
                      <CheckCircle2 className="h-3 w-3" />Uploaded
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{transcriptOpen ? '−' : '+'}</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4 space-y-3">
              <p className="text-xs text-muted-foreground">Paste or upload a transcript to enable AI-powered auto-fill of summary, job mappings, and filters.</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="transcript-textarea" className="text-sm font-medium">Transcript Content</Label>
                  <span className={`text-xs ${transcriptTextInput.length > 500000 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                    {transcriptTextInput.length.toLocaleString()} / 500,000 chars
                  </span>
                </div>
                <Textarea id="transcript-textarea" value={transcriptTextInput} onChange={(e) => setTranscriptTextInput(e.target.value)} placeholder="Paste transcript content here..." className="min-h-[300px] font-mono text-sm resize-y" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={handleSaveTranscript} disabled={isSavingTranscript || !transcriptTextInput.trim() || transcriptTextInput.length > 500000} variant="outline" className="gap-2">
                  {isSavingTranscript ? (<><Loader2 className="h-4 w-4 animate-spin" />Saving...</>) : (<><CheckCircle2 className="h-4 w-4" />Save Transcript</>)}
                </Button>
                <label className="cursor-pointer">
                  <input type="file" accept=".txt,.vtt,.srt" className="hidden" onChange={handleTranscriptUpload} disabled={isUploadingTranscript} />
                  <Button variant="ghost" size="sm" asChild disabled={isUploadingTranscript}>
                    <span className="gap-1">{isUploadingTranscript ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-3 w-3 mr-1" />Upload File</>}</span>
                  </Button>
                </label>
              </div>
              {transcriptStatus?.hasTranscript && (
                <div className="flex items-center justify-between p-3 bg-accent/30 rounded-md">
                  <div className="text-sm">
                    <p className="font-medium flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-600" />Transcript available</p>
                    <p className="text-xs text-muted-foreground">Source: {transcriptStatus.source} | {transcriptStatus.charCount?.toLocaleString()} chars saved</p>
                  </div>
                </div>
              )}
              {transcriptStatus?.hasTranscript && (
                <>
                  <Button onClick={handleAnalyzeTranscript} disabled={isAnalyzingTranscript} className="w-full gap-2" variant="default">
                    {isAnalyzingTranscript ? (<><Loader2 className="h-4 w-4 animate-spin" />Analyzing transcript...</>) : (<><Wand2 className="h-4 w-4" />Analyze & Auto-Fill Artifact</>)}
                  </Button>
                  {analysisResult && (
                    <div className="text-xs text-muted-foreground bg-green-500/10 border border-green-500/30 p-2 rounded-md">
                      <p className="font-medium text-green-700 mb-1">
                        ✓ {analysisResult.analyzed_at ? 'Previously analyzed' : 'Analysis applied'}
                        {analysisResult.analyzed_at && <span className="font-normal text-muted-foreground ml-2">({new Date(analysisResult.analyzed_at).toLocaleDateString()})</span>}
                      </p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {analysisResult.skill_depth && <span>Skill: {analysisResult.skill_depth}</span>}
                        {analysisResult.summary && <span>Summary: ✓</span>}
                        <span>Jobs: {analysisResult.job_mappings?.length || 0}</span>
                        {analysisResult.vehicle_fitment && analysisResult.vehicle_fitment.length > 0 && <span>Vehicles: {analysisResult.vehicle_fitment.length}</span>}
                        <span>DTCs: {analysisResult.dtc_codes?.length || 0}</span>
                        <span>Keywords: {analysisResult.keywords?.length || 0}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Phase Q1 — admin-only draft quiz generation. Self-disables when
                  there is no transcript; server enforces manager/admin auth.
                  Creates a DRAFT only — never approves, never technician-visible. */}
              <GenerateDraftQuizPanel
                artifactId={artifact.id}
                hasTranscript={!!transcriptStatus?.hasTranscript}
              />
            </CollapsibleContent>
          </Collapsible>

          {/* Job-Specific Mapping Section */}
          <Collapsible open={jobMappingsOpen} onOpenChange={setJobMappingsOpen} className="border rounded-lg">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors text-left">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Job-Specific Mapping</span>
                  {jobMappings.length > 0 && <Badge variant="secondary" className="text-xs">{jobMappings.length}</Badge>}
                </div>
                <span className="text-xs text-muted-foreground">{jobMappingsOpen ? '−' : '+'}</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4 space-y-3">
              <p className="text-xs text-muted-foreground">Link this training to specific jobs. When a ticket has these jobs, this training will be prioritized.</p>
              
              {analysisJobSuggestions.length > 0 && (
                <div className="p-3 border border-yellow-500/30 bg-yellow-500/5 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-yellow-600">AI Suggestions ({analysisJobSuggestions.length}) — Not Saved</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        const existingIds = new Set(jobMappings.map(j => j.job_id));
                        const newMappings = analysisJobSuggestions.filter(s => !existingIds.has(s.job_id)).map(s => ({ job_id: s.job_id, relevance_weight: s.relevance_weight }));
                        setJobMappings(prev => [...prev, ...newMappings]);
                        setAnalysisJobSuggestions([]);
                        toast.success(`Applied ${newMappings.length} suggestions`);
                      }}>Apply All</Button>
                      <Button variant="ghost" size="sm" onClick={() => setAnalysisJobSuggestions([])}>Dismiss</Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {analysisJobSuggestions.map(s => (
                      <div key={s.job_id} className="flex items-center justify-between text-sm p-2 bg-background/50 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="truncate">{allJobs?.find(j => j.id === s.job_id)?.label || s.job_id}</span>
                          {s.mapping_kind && <span className="ml-2 text-xs text-muted-foreground">({s.mapping_kind})</span>}
                          {s.evidence_quote && <p className="text-xs text-muted-foreground italic mt-0.5 truncate">"{s.evidence_quote}"</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                            if (!jobMappings.some(jm => jm.job_id === s.job_id)) {
                              setJobMappings(prev => [...prev, { job_id: s.job_id, relevance_weight: s.relevance_weight }]);
                            }
                            setAnalysisJobSuggestions(prev => prev.filter(x => x.job_id !== s.job_id));
                          }}><Plus className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setAnalysisJobSuggestions(prev => prev.filter(x => x.job_id !== s.job_id))}><X className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {rejectedProposals.length > 0 && (
                <Collapsible open={rejectedOpen} onOpenChange={setRejectedOpen}>
                  <CollapsibleTrigger asChild>
                    <button className="w-full flex items-center justify-between p-3 border border-red-500/30 bg-red-500/5 rounded-lg hover:bg-red-500/10 transition-colors text-left">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="text-sm font-medium text-red-600">Rejected Proposals ({rejectedProposals.length})</span>
                        {gateStats && <span className="text-xs text-muted-foreground">— {gateStats.total_proposed} proposed, {gateStats.validated} validated</span>}
                      </div>
                      <span className="text-xs text-muted-foreground">{rejectedOpen ? '−' : '+'}</span>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-2">
                    {gateStats && Object.keys(gateStats.rejection_by_gate).length > 0 && (
                      <div className="flex flex-wrap gap-1 p-2 bg-muted/50 rounded text-xs">
                        <span className="text-muted-foreground mr-1">Gates:</span>
                        {Object.entries(gateStats.rejection_by_gate).map(([gate, count]) => (
                          <Badge key={gate} variant="outline" className="text-xs">{gate}: {count}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {rejectedProposals.map((r, idx) => (
                        <div key={`${r.job_id}-${idx}`} className="text-sm p-2 bg-red-500/5 rounded border border-red-500/20">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{r.job_label || allJobs?.find(j => j.id === r.job_id)?.label || r.job_id}</p>
                              <div className="mt-1 space-y-0.5">
                                {r.gate_failures.map((gf, gIdx) => (
                                  <p key={gIdx} className="text-xs text-red-600">
                                    <span className="font-mono bg-red-500/10 px-1 rounded">{gf.gate}</span>
                                    {gf.reason && <span className="text-muted-foreground ml-1">— {gf.reason}</span>}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => { setRejectedProposals([]); setGateStats(null); }}>Clear</Button>
                  </CollapsibleContent>
                </Collapsible>
              )}
              
              {availableJobs.length > 0 && (
                <Popover open={jobSearchOpen} onOpenChange={setJobSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={jobSearchOpen} className="w-full justify-between h-9">
                      Add a job...<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search jobs..." />
                      <CommandList>
                        <CommandEmpty>No jobs found.</CommandEmpty>
                        <CommandGroup>
                          {availableJobs.map((job) => (
                            <CommandItem key={job.id} value={`${job.label} ${job.category_key}`} onSelect={() => { handleAddJobMapping(job.id); setJobSearchOpen(false); }}>
                              <span className="truncate">{job.label}</span>
                              <span className="text-muted-foreground ml-1 text-xs">({job.category_key})</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
              
              {jobMappings.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">No jobs mapped yet</p>
              ) : (
                <div className="space-y-2">
                  {jobMappings.map((mapping) => (
                    <div key={mapping.job_id} className="flex items-center gap-2 p-2 bg-accent/30 rounded-md">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{getJobLabel(mapping.job_id)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">Weight:</span>
                          <Slider value={[mapping.relevance_weight]} onValueChange={([v]) => handleUpdateJobWeight(mapping.job_id, v)} min={0.1} max={2.0} step={0.1} className="w-24" />
                          <span className="text-xs font-mono w-8">{mapping.relevance_weight.toFixed(1)}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveJobMapping(mapping.job_id)} className="h-8 w-8 p-0 flex-shrink-0"><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Vehicle Fitment Section */}
          <Collapsible open={fitmentOpen} onOpenChange={setFitmentOpen} className="border rounded-lg">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors text-left">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Vehicle Fitment</span>
                  {fitmentRows.length > 0 && <Badge variant="secondary" className="text-xs">{fitmentRows.length}</Badge>}
                </div>
                <span className="text-xs text-muted-foreground">{fitmentOpen ? '−' : '+'}</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4 space-y-3">
              <p className="text-xs text-muted-foreground">Tag this training with specific vehicles. When a ticket matches the year/make/model, this training will be highly prioritized.</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Year</Label>
                  <Select value={newFitment.year} onValueChange={(val) => setNewFitment({ year: val, make: '', model: '', engine: '' })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select year..." /></SelectTrigger>
                    <SelectContent>{fitmentYears.map(year => <SelectItem key={year} value={String(year)}>{year}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Make</Label>
                  <Select value={newFitment.make} onValueChange={(val) => setNewFitment(prev => ({ ...prev, make: val, model: '', engine: '' }))} disabled={!newFitment.year || isLoadingFitmentMakes}>
                    <SelectTrigger className="h-9">{isLoadingFitmentMakes ? <Loader2 className="h-4 w-4 animate-spin" /> : <SelectValue placeholder="Select make..." />}</SelectTrigger>
                    <SelectContent>{fitmentMakes.map(make => <SelectItem key={make} value={make}>{make}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Model</Label>
                  <Select value={newFitment.model} onValueChange={(val) => setNewFitment(prev => ({ ...prev, model: val, engine: '' }))} disabled={!newFitment.make || isLoadingFitmentModels}>
                    <SelectTrigger className="h-9">{isLoadingFitmentModels ? <Loader2 className="h-4 w-4 animate-spin" /> : <SelectValue placeholder="Select model..." />}</SelectTrigger>
                    <SelectContent>{fitmentModels.map(model => <SelectItem key={model} value={model}>{model}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Engine</Label>
                  <Select value={newFitment.engine} onValueChange={(val) => setNewFitment(prev => ({ ...prev, engine: val }))} disabled={!newFitment.model || isLoadingFitmentEngines}>
                    <SelectTrigger className="h-9">{isLoadingFitmentEngines ? <Loader2 className="h-4 w-4 animate-spin" /> : <SelectValue placeholder="Select engine..." />}</SelectTrigger>
                    <SelectContent>{fitmentEngines.map(engine => <SelectItem key={engine} value={engine}>{engine}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button type="button" size="sm" variant="outline" className="w-full h-9" onClick={handleAddFitment} disabled={!newFitment.year && !newFitment.make && !newFitment.model && !newFitment.engine}>
                  <Plus className="h-4 w-4 mr-2" />Add Fitment
                </Button>
              </div>
              {fitmentRows.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">No vehicle fitments added yet</p>
              ) : (
                <div className="space-y-2">
                  {fitmentRows.map((fitment, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-accent/30 rounded-md">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{[fitment.year, fitment.make, fitment.model].filter(Boolean).join(' ') || 'Any vehicle'}</p>
                        {fitment.engine && <p className="text-xs text-muted-foreground">Engine: {fitment.engine}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{fitment.confidence === 'explicit' ? 'Manual' : 'Inferred'}</Badge>
                          <span className="text-xs text-muted-foreground">Boost: +{fitment.boost}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveFitment(index)} className="h-8 w-8 p-0 flex-shrink-0"><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Vehicle & Notes Filtering Section */}
          <Collapsible open={filteringOpen} onOpenChange={setFilteringOpen} className="border rounded-lg">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors text-left">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Vehicle & Notes Filtering</span>
                  {(formData.vehicle_fuel_types.length > 0 || formData.dtc_codes.length > 0 || formData.keywords.length > 0) && (
                    <Badge variant="secondary" className="text-xs">{formData.vehicle_fuel_types.length + formData.dtc_codes.length + formData.keywords.length}</Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{filteringOpen ? '−' : '+'}</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Filter when this training appears based on vehicle type, DTC codes, or keywords in workflow context (complaints, diagnostics, inspection, notes).</p>
                <Button variant="outline" size="sm" onClick={handleAutoSuggestTags} disabled={isSuggestingTags} className="shrink-0 gap-1">
                  {isSuggestingTags ? (<><Loader2 className="h-3 w-3 animate-spin" />Analyzing...</>) : (<><Wand2 className="h-3 w-3" />Auto-suggest</>)}
                </Button>
              </div>
              {suggestConfidenceNotes && (
                <div className="text-xs text-muted-foreground bg-accent/30 p-2 rounded-md flex items-start gap-2">
                  <Bot className="h-3 w-3 mt-0.5 shrink-0" /><span>{suggestConfidenceNotes}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Vehicle Fuel Types</Label>
                <p className="text-xs text-muted-foreground">Empty = applies to all vehicles</p>
                <div className="flex flex-wrap gap-2">
                  {['gas', 'diesel', 'hybrid', 'ev'].map((fuel) => (
                    <div key={fuel} className="flex items-center space-x-2">
                      <Checkbox id={`fuel-${fuel}`} checked={formData.vehicle_fuel_types.includes(fuel)} onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          vehicle_fuel_types: checked ? [...prev.vehicle_fuel_types, fuel] : prev.vehicle_fuel_types.filter(f => f !== fuel)
                        }));
                      }} />
                      <Label htmlFor={`fuel-${fuel}`} className="text-sm capitalize">{fuel === 'ev' ? 'EV' : fuel}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">DTC Codes</Label>
                <p className="text-xs text-muted-foreground">Training will be boosted when these codes appear in workflow context</p>
                <div className="flex gap-2">
                  <Input placeholder="e.g., P0300" value={newDtcCode} onChange={(e) => setNewDtcCode(e.target.value.toUpperCase())} className="h-8 flex-1" onKeyDown={(e) => {
                    if (e.key === 'Enter' && newDtcCode.trim()) {
                      e.preventDefault();
                      if (!formData.dtc_codes.includes(newDtcCode.trim())) {
                        setFormData(prev => ({ ...prev, dtc_codes: [...prev.dtc_codes, newDtcCode.trim()] }));
                      }
                      setNewDtcCode('');
                    }
                  }} />
                  <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => {
                    if (newDtcCode.trim() && !formData.dtc_codes.includes(newDtcCode.trim())) {
                      setFormData(prev => ({ ...prev, dtc_codes: [...prev.dtc_codes, newDtcCode.trim()] }));
                      setNewDtcCode('');
                    }
                  }}><Plus className="h-3 w-3" /></Button>
                </div>
                {formData.dtc_codes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1 max-w-full">
                    {formData.dtc_codes.map((code) => (
                      <Badge key={code} variant="outline" className="text-xs gap-1 font-mono">
                        {code}
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, dtc_codes: prev.dtc_codes.filter(c => c !== code) }))} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Keywords</Label>
                <p className="text-xs text-muted-foreground">Training will be boosted when these words appear in workflow context</p>
                <div className="flex gap-2">
                  <Input placeholder="e.g., misfire" value={newKeyword} onChange={(e) => setNewKeyword(e.target.value.toLowerCase())} className="h-8 flex-1" onKeyDown={(e) => {
                    if (e.key === 'Enter' && newKeyword.trim()) {
                      e.preventDefault();
                      if (!formData.keywords.includes(newKeyword.trim())) {
                        setFormData(prev => ({ ...prev, keywords: [...prev.keywords, newKeyword.trim()] }));
                      }
                      setNewKeyword('');
                    }
                  }} />
                  <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => {
                    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
                      setFormData(prev => ({ ...prev, keywords: [...prev.keywords, newKeyword.trim()] }));
                      setNewKeyword('');
                    }
                  }}><Plus className="h-3 w-3" /></Button>
                </div>
                {formData.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1 max-w-full">
                    {formData.keywords.map((kw) => (
                      <Badge key={kw} variant="outline" className="text-xs gap-1">
                        {kw}
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, keywords: prev.keywords.filter(k => k !== kw) }))} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Capabilities Section */}
          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:underline">
              ▶ Capabilities
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <ArtifactCapabilityEditor artifactId={artifact.id} />
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="sm:mr-auto"><Trash2 className="h-4 w-4 mr-2" />Remove</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove Artifact</AlertDialogTitle>
                <AlertDialogDescription>This will hide "{artifact.title}" from the training catalog. You can restore it later by setting it back to Active.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  {isDeleting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Removing...</>) : 'Remove'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>) : 'Save Changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
